import React, { useEffect, useRef, useState } from 'react';
import { AddressAutofill } from '@mapbox/search-js-react';

const Section3 = ({
  formData,
  handleInputChange,
  clientSearchTerm,
  handleClientSearchChange,
  matchingClients,
  handleClientSelect,
  isFetchingClients,
  currentSection
}) => {
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const dropdownRef = useRef(null);
  const languageOptions = [
    "english",
    "spanish",
    "french",
    "german",
    "chinese",
    "japanese",
    "arabic",
    "pashto",
    "turkish",
    "nigerian",
    "dari",
    "other",
  ];
  const [noAddress, setNoAddress] = useState(false);

  const handleOtherLanguageChange = (e) => {
    handleInputChange({
      target: {
        name: 'otherLanguage',
        value: e.target.value.toLowerCase(),
      },
    });
  };

  const handleSearchInputChange = (e) => {
    handleInputChange(e);
    handleClientSearchChange(e);
    setIsDropdownVisible(true); // Show dropdown when typing
  };

  const handleInputChangeLowercase = (e) => {
    const { name, value } = e.target;
    handleInputChange({ target: { name, value: value.toLowerCase() } });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      {/* Section 3 */}
      <div className="form-section flyer-section">
        <div className="flyer-header">Client Information</div>
        <label className="required-asterisk">Client Name:</label>
        <input
          type="text"
          name="name"
          value={clientSearchTerm}
          onChange={handleSearchInputChange}
          onBlur={handleInputChangeLowercase}
          placeholder="Your answer"
          required={currentSection === 3}
          autoComplete="new-client-name"
        />
        {isDropdownVisible && matchingClients.length > 0 && (
          <ul ref={dropdownRef} className="client-suggestions">
            {matchingClients
              .slice()
              .sort((a, b) => {
                const fullNameA = `${a.name.toLowerCase()} ${a.last_name.toLowerCase()}`;
                const fullNameB = `${b.name.toLowerCase()} ${b.last_name.toLowerCase()}`;
                return fullNameA.localeCompare(fullNameB);
              })
              .map((client) => (
                <li
                  key={client.id}
                  onClick={() => {
                    handleClientSelect(client);
                    setIsDropdownVisible(false);
                  }}
                >
                  {client.name} {client.last_name}
                </li>
              ))}
          </ul>
        )}
        {isFetchingClients && <p>Loading clients...</p>}
      </div>

      <div className="form-section">
        <label className="required-asterisk">Client Last Name:</label>
        <input
          type="text"
          name="lastName"
          value={formData.lastName}
          onChange={handleInputChange}
          placeholder="Your answer"
          required={currentSection === 3}
        />
      </div>

      <div className="form-section">
        <label className="required-asterisk">Phone Number:</label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleInputChange}
          placeholder="Your answer"
          pattern="[0-9]*"
          inputMode="numeric"
          required={currentSection === 3}
        />
      </div>

      <div className="form-section">
        <label className="required-asterisk">Email:</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          placeholder="Your answer"
          required={currentSection === 3}
        />
      </div>

      <div className="form-section">
        <label className="required-asterisk">Address:</label>
        <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={noAddress}
            onChange={(e) => {
              const isChecked = e.target.checked;
              setNoAddress(isChecked);
              const value = isChecked ? "N/A" : "";
              handleInputChange({ target: { name: "address address-search", value } });
              handleInputChange({ target: { name: "city", value } });
              handleInputChange({ target: { name: "county", value } });
              handleInputChange({ target: { name: "zip_code", value } });
            }}
          />
          <span style={{ marginLeft: "5px" }}>No Address</span>
        </label>

        <AddressAutofill
          accessToken="pk.eyJ1IjoiZXVsaXNlcy1mcmFuY28iLCJhIjoiY203YzUyZHV4MDBxejJtb3N5dGI5dnF1cCJ9.9Zzj5B9hOEcQyw5AGHOrCA"
          onRetrieve={(res) => {
            if (!noAddress) {
              const context = res.features[0].properties;
              handleInputChange({ target: { name: "address", value: context?.address_line1 || "" } });
              handleInputChange({ target: { name: "city", value: context?.address_level2 || "" } });
              handleInputChange({ target: { name: "county", value: context?.district || "" } });
              handleInputChange({ target: { name: "zip_code", value: context?.postcode || "" } });
            }
          }}
        >
          <input
            name="address"
            placeholder="Address"
            type="text"
            autoComplete="address-line1"
            value={formData.address}
            onChange={handleInputChange}
            disabled={noAddress}
            required={currentSection === 3}
            style={{ width: "100%", resize: "vertical" }}
          />
        </AddressAutofill>
      </div>

      <div className="form-section">
        <label className="required-asterisk">City</label>
        <input
          name="city"
          placeholder="City"
          type="text"
          autoComplete="address-level2"
          value={formData.city}
          required={currentSection === 3}
          onChange={handleInputChange}
        />
      </div>

      <div className="form-section">
        <label className="required-asterisk">County</label>
        <input
          name="county"
          placeholder="County"
          type="text"
          value={formData.county}
          required={currentSection === 3}
          onChange={handleInputChange}
        />
      </div>

      <div className="form-section">
        <label className="required-asterisk">Zip Code</label>
        <input
          name="zip_code"
          placeholder="Zip Code"
          type="text"
          autoComplete="postcode"
          value={formData.zip_code}
          required={currentSection === 3}
          onChange={handleInputChange}
        />
      </div>

      <div className="form-section">
        <label className="required-asterisk">Preferred Language</label>
        <div className="check-group">
          {languageOptions.map((language) => (
            <label key={language}>
              <input
                type="checkbox"
                name="prefered_lang"
                value={language}
                checked={formData.prefered_lang?.includes(language)}
                onChange={handleInputChange}
              />
              {language.charAt(0).toUpperCase() + language.slice(1)}
            </label>
          ))}
          {formData.prefered_lang.includes("other") && (
            <input
              type="text"
              name="otherLanguage"
              value={formData.otherLanguage || ""}
              onChange={handleOtherLanguageChange}
              placeholder="Your answer"
              required={currentSection === 3}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default Section3;
